// What the Overview shows, as pure functions of the API data, so tests can assert the mapping
// without rendering. Every `now` is a parameter: the page ticks it, tests pin it.
import type {
  AuditAction,
  AuditEvent,
  Device,
  DeviceStatus,
  MetricsRange,
  MonthToDateCost,
  Relay,
  RelayState,
} from '../../api/types';
import { formatDateTime } from '../../lib/format';
import { onlineIds, presence, type Presence } from '../../lib/status';

const MINUTE = 60_000;

/**
 * When the relay agent will stop an idle relay, as epoch ms; null unless it is running.
 *
 * The agent's rule (CONTRACT 7, step 6) is `idleSince = max(latest handshake, agent start)`,
 * stop once `now - idleSince >= timeout`. `since` stands in for the agent start. The relay row's
 * `lastHandshakeAt` is only refreshed on agent peer syncs, so this is an estimate.
 */
export function idleStopDeadline(relay: Relay | null, idleMinutes: number): number | null {
  if (relay?.state !== 'running') return null;
  const since = Date.parse(relay.since);
  if (Number.isNaN(since)) return null;
  const handshake = relay.lastHandshakeAt ? Date.parse(relay.lastHandshakeAt) : Number.NaN;
  const idleSince = Number.isNaN(handshake) ? since : Math.max(handshake, since);
  return idleSince + idleMinutes * MINUTE;
}

/** When the relay stops if nothing handshakes: the API's figure, or the browser's estimate. */
export interface AutoStop {
  /** Epoch ms. */
  at: number;
  /** True when made in the browser because the API sent no `autoStopAt`. */
  estimate: boolean;
}

/**
 * The idle-stop deadline: `relay.autoStopAt` while running (the agent's own clock, ±1 tick), and
 * the `idleStopDeadline` estimate only when the API has none (an older API, or no idle timeout).
 */
export function autoStop(relay: Relay | null, idleMinutes: number): AutoStop | null {
  if (relay?.state !== 'running') return null;
  const at = relay.autoStopAt ? Date.parse(relay.autoStopAt) : Number.NaN;
  if (Number.isFinite(at)) return { at, estimate: false };
  const estimate = idleStopDeadline(relay, idleMinutes);
  return estimate === null ? null : { at: estimate, estimate: true };
}

/** The countdown's tooltip: what the deadline is based on. */
export function autoStopHint(stop: AutoStop, idleMinutes: number): string {
  return stop.estimate
    ? `Estimate: the agent stops the relay ${idleMinutes} min after the later of the last handshake and the start. The relay records handshakes only on its periodic peer syncs.`
    : 'The relay agent stops the relay then unless a device handshakes first (±1 min, the agent checks once a minute).';
}

/** Whole minutes left until `deadline`, rounded up and never negative ("in 0 min" = any moment now). */
export function minutesLeft(deadline: number, now: number): number {
  return Math.max(0, Math.ceil((deadline - now) / MINUTE));
}

/** A duration as its two largest units: "22s", "14m 22s", "3h 05m", "2d 4h". */
export function duration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86_400);
  const h = Math.floor((total % 86_400) / 3_600);
  const m = Math.floor((total % 3_600) / 60);
  const s = total % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/** "up 14m 22s": how long the relay has been in its current state; null for an unparseable `since`. */
export function uptime(relay: Relay, now: number): string | null {
  const since = Date.parse(relay.since);
  return Number.isNaN(since) ? null : `up ${duration(now - since)}`;
}

/** "2s ago", "11m ago", "3h ago", "2d ago"; "never" for null. */
export function ago(iso: string | null, now: number): string {
  if (!iso) return 'never';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '–';
  const sec = Math.max(0, Math.floor((now - t) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3_600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86_400) return `${Math.floor(sec / 3_600)}h ago`;
  return `${Math.floor(sec / 86_400)}d ago`;
}

/** The header's time-range tabs; each picks the range the live-throughput band reads. */
export const OVERVIEW_RANGES: { id: MetricsRange; label: string; hint: string }[] = [
  { id: '1h', label: 'Live', hint: 'last hour' },
  { id: '24h', label: 'Last 24 h', hint: 'last 24 h' },
  { id: '7d', label: 'Last 7 d', hint: 'last 7 d' },
];

/** "3 / 8": devices online or live of all enrolled; "– / 8" before statuses load. */
export function onlineText(
  devices: readonly Device[] | null,
  statuses: readonly DeviceStatus[] | null,
  live: ReadonlySet<string>,
  now: number,
): string {
  const total = devices?.length ?? '–';
  if (!devices || !statuses) return `– / ${total}`;
  return `${onlineIds(devices, statuses, live, now).size} / ${total}`;
}

/** Mockup 01 lists seven devices; "View all" leads to the rest. */
export const DEVICE_ROWS = 7;

export interface DeviceRow {
  device: Device;
  status: DeviceStatus | undefined;
  /** null until the statuses load. */
  presence: Presence | null;
}

const RANK: Record<Presence, number> = { live: 0, online: 1, offline: 2 };

/**
 * The Overview's device list: live first, then online, then offline, each group in the order the
 * API gave (by name), cut to max rows. Before the statuses load the order is left alone.
 */
export function orderDevices(
  devices: readonly Device[],
  statuses: readonly DeviceStatus[] | null,
  live: ReadonlySet<string>,
  now: number,
  max: number = DEVICE_ROWS,
): DeviceRow[] {
  const byId = new Map((statuses ?? []).map((s) => [s.deviceId, s]));
  const rows = devices.map((device) => {
    const status = byId.get(device.id);
    return { device, status, presence: statuses ? presence(device.id, status, live, now) : null };
  });
  // Array.prototype.sort is stable, so each group keeps the name order.
  if (statuses) rows.sort((a, b) => RANK[a.presence!] - RANK[b.presence!]);
  return rows.slice(0, max);
}

/** "1.47 €" style money in the console's language; a bare amount when Azure has no currency yet. */
export function formatCost(amount: number, currency: string | null): string {
  if (!currency) return amount.toFixed(2);
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency }).format(amount);
  } catch {
    // Not an ISO 4217 code Intl knows: still show it rather than fail.
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export interface CostView {
  /** "€1.47"; "—" when it cannot be shown, "–" while loading. */
  value: string;
  /** A cached figure from earlier this month (Azure failed this time). */
  cached: boolean;
  /** Why "—", or when the figure was fetched; null while loading. */
  tooltip: string | null;
}

/** The Cost card and status bar: GET /cost/month-to-date is admin-only and may answer 502. */
export function costView(cost: MonthToDateCost | null, error: string | null, isAdmin: boolean): CostView {
  if (!isAdmin) return { value: '—', cached: false, tooltip: 'Only admins can read the cost.' };
  if (!cost) return error ? { value: '—', cached: false, tooltip: error } : { value: '–', cached: false, tooltip: null };
  const when = formatDateTime(cost.fetchedAt);
  return {
    value: formatCost(cost.amount, cost.currency),
    cached: cost.stale,
    tooltip: cost.stale
      ? `Cached: Azure did not answer, this is the figure from ${when}.`
      : `Resource group vpn, month to date, as of ${when}.`,
  };
}

/** The four states in the order the relay walks them; the state machine draws one segment each. */
export const RELAY_STEPS: readonly RelayState[] = ['stopped', 'starting', 'running', 'stopping'];

export function relayStepIndex(state: RelayState): number {
  return RELAY_STEPS.indexOf(state);
}

/** "Running" from "running". */
export const stateWord = (state: RelayState) => state[0]!.toUpperCase() + state.slice(1);

/** A WireGuard key the way the mockup prints it: "kQ3fJ8pX…mZc1=". */
export function shortKey(key: string): string {
  return key.length <= 16 ? key : `${key.slice(0, 8)}…${key.slice(-5)}`;
}

/** Local wall-clock "14:22:06" for the Activity time column. */
export function clockTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return [d.getHours(), d.getMinutes(), d.getSeconds()].map((n) => String(n).padStart(2, '0')).join(':');
}

/** The Event column's text for each audit action (mockup 01). A Record, so a new action fails the build. */
export const AUDIT_LABELS: Record<AuditAction, string> = {
  'relay.started': 'Relay started',
  'relay.stopped': 'Relay stopped',
  'relay.start_failed': 'Relay start failed',
  'relay.stop_failed': 'Relay stop failed',
  'device.enrolled': 'Enrolled device',
  'device.revoked': 'Revoked device',
  'device.renamed': 'Renamed device',
  'device.tagged': 'Tagged device',
  'device.moved': 'Moved device',
  'address.reserved': 'Reserved address',
  'address.released': 'Released address',
  'policy.published': 'Published policy',
  'network.created': 'Created network',
  'network.updated': 'Updated network',
  'network.deleted': 'Deleted network',
  'device.routed': 'Changed site routes',
  'site.claimed': 'Claimed site',
};

/**
 * Event and Detail columns for one audit event. A publish's detail reads
 * "Published policy v14 — 3 rules changed"; the version moves into the Event
 * ("Published policy v14") and the rest stays as the Detail ("3 rules changed").
 */
export function auditLabel(e: Pick<AuditEvent, 'action' | 'detail'>): { event: string; detail: string } {
  const label = AUDIT_LABELS[e.action] ?? e.action;
  if (e.action === 'policy.published') {
    const m = /^Published policy (v\d+)\s*(?:[—–-]\s*(.*))?$/.exec(e.detail);
    if (m) return { event: `${label} ${m[1]}`, detail: m[2] ?? '' };
  }
  return { event: label, detail: e.detail };
}
